import { observer } from 'mobx-react-lite'
import React, { FunctionComponent, useMemo } from 'react'
import orderBy from 'lodash/orderBy'
import styled from 'styled-components'
import Button from '../../components/Button'
import ContestantThumbnail from '../../components/ContestantThumbnail'
import Dropdown from '../../components/Dropdown'
import Message from '../../components/Message'
import Text from '../../components/Text'
import { colors, device, spacing } from '../../styles/_var'
import { ContestantWithBio, ICategory } from '../../utils/interfaces'
import { AllContestantWrapper } from './AllContestants'
import votingRoundStore from '../../stores/votingRoundStore'
import contestantStore from '../../stores/contestantStore'
import uiStore from '../../stores/uiStore'
import seasonStore from '../../stores/seasonStore'
import CompetitionCategoryDropdown from '../../components/CompetitionCategoryDropdown'

interface IResults {
  editMode?: boolean;
  selectedCategory: ICategory
  categories: ICategory[]
  onSelectCategory: (category: ICategory) => void
}

const Results: FunctionComponent<IResults> = ({ 
  editMode,
  selectedCategory,
  categories,
  onSelectCategory
}) => {
  const {
    fetchingVotingRound,
    votingRounds,
    selectedVotingRound,
    hasVotingRounds
  } = votingRoundStore
  const { contestants } = contestantStore

  const votingRoundsDropdownList = useMemo(() => {
    return votingRounds.map(({ label }) => label)
  }, [votingRounds])

  if (!hasVotingRounds) return null

  const activeContestants = orderBy(votingRoundStore.filterContestantsPageByCategory(selectedVotingRound, selectedCategory), 'votes', 'desc')

  const getContestantByUsername = (username: string): ContestantWithBio => {
    return contestants.find(c => c.username === username) || ({} as ContestantWithBio)
  }

  if (fetchingVotingRound && !activeContestants.length) {
    return <Message message="Fetching voting rounds" />
  }

  if (!votingRounds.length) {
    return <Message message="There are no voting rounds." type="error" />
  }

  const totalVotes = activeContestants.reduce((acc, c) => acc + c.votes, 0)

  const calculateVotePercent = (vote: number) => {
    return totalVotes === 0 ? '0%' : `${Math.floor((vote/totalVotes * 100))}%`
  }

  return (
    <Container>
      <ActionButtons>
        <CompetitionCategoryDropdown  
          selectedCategory={selectedCategory}
          categories={categories}
          onSelectCategory={onSelectCategory}
        />
        <Dropdown
          style={{marginTop: spacing.sm}}
          aria-label="voting rounds"
          list={votingRoundsDropdownList}
          onItemSelect={label => votingRoundStore.setSelectedVotingRoundByLabel(label)}
          selected={selectedVotingRound.label} />
      </ActionButtons>
      <AllContestantWrapper>
        {activeContestants.map((contestant, index) => {
          const activeContestant = getContestantByUsername(contestant.user)
          return (
            <ContestantThumbnail
              winner={votingRoundStore.isWinner(activeContestant.id || 0, selectedCategory, selectedVotingRound?.id)}
              variant="results"
              key={index}
              votes={contestant.votes}
              showVoteCount={selectedVotingRound?.voting_option?.show_vote_count}
              votePercentage={calculateVotePercent(contestant.votes)}
              {...activeContestant}
              evicted={contestant.evicted}
            />
          )
        })}
      </AllContestantWrapper>
      {activeContestants.length === 0 && (
        <Text variant="medium" center weight="semi-bold" color={colors.red}>This category has no contestants</Text>
      )}
      { editMode && <EndVotingButton><Text variant="medium">End Voting Round</Text></EndVotingButton>}
      { !editMode && (
        <ReloadButton
          variant="secondary"
          onClick={() => {
            votingRoundStore.fetchVotingRounds(
              seasonStore.selectedSeasonId,
              () => uiStore.showToastMessage({
                message: 'Page has been refreshed',
                duration: 3000,
              })
            )
          }}
        >
          Reload Page
        </ReloadButton>
      )}
    </Container>
  )
}

export default observer(Results)

const Container = styled.div`
  display: flex;
  flex-direction: column;

  & > div:first-child {
    margin: 0 0 ${spacing.xs} 0;
  }
`

const EndVotingButton = styled(({ ...rest }) => <Button {...rest} />)`
  margin: auto;
  width: 25rem;
  display: block;
  margin-bottom: ${spacing.xs};
  max-width: 100%;
`

const ReloadButton = styled(({ ...rest }) => <Button {...rest} />)`
  display: block;
  margin: ${spacing.sm} auto;
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${device.tablet} {
    flex-direction: row;
  }
`
